import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';
import { useNavigate } from 'react-router-dom';
import API from '../../services/api';
import './Analytics.css';

const sc = v => v == null ? '#94a3b8' : v >= 75 ? '#16a34a' : v >= 60 ? '#2563eb' : v >= 40 ? '#d97706' : '#dc2626';
const fmt = (v, s = '') => v == null ? '—' : `${v}${s}`;
const RL = { top_performer: 'Top Performer', placement_ready: 'Placement Ready', moderate: 'Moderate', needs_improvement: 'Needs Improvement' };
const DIST_COLORS = ['#dc2626', '#d97706', '#2563eb', '#16a34a'];

const Kpi = ({ label, value, suffix, tone, sub }) => (
  <div className="axh-kpi">
    <div className="v" style={tone ? { color: tone } : undefined}>{fmt(value, suffix)}</div>
    <div className="l">{label}</div>
    {sub && <div className="sub">{sub}</div>}
  </div>
);

const Bars = ({ items, lk, vk, max = 100, unit = '%' }) => (
  <div className="axh-bars">
    {items.length === 0 ? <div className="axh-empty">No data yet</div> : items.map((it, i) => (
      <div className="axh-bar-row" key={i}>
        <span className="axh-bar-label" title={it[lk]}>{it[lk]}</span>
        <div className="axh-bar-track"><div className="axh-bar-fill" style={{ width: `${Math.min((it[vk] || 0) / max * 100, 100)}%`, background: sc(it[vk]) }} /></div>
        <span className="axh-bar-val" style={{ color: sc(it[vk]) }}>{it[vk] == null ? '—' : `${it[vk]}${unit}`}</span>
      </div>
    ))}
  </div>
);

const Section = ({ title, sub, children }) => (
  <section className="axh-section">
    {title && <div className="axh-section-head"><h2>{title}</h2>{sub && <p>{sub}</p>}</div>}
    {children}
  </section>
);

const TABS = [
  { key: 'overview', label: 'Executive Overview' },
  { key: 'health', label: 'College Health' },
  { key: 'learning', label: 'Learning Analytics' },
  { key: 'behaviour', label: 'Behaviour' },
  { key: 'placement', label: 'Placement Readiness' },
  { key: 'intervention', label: 'Intervention' },
  { key: 'tests', label: 'Test Analytics' },
];

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [testsData, setTestsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');
  const [branch, setBranch] = useState('');
  const [batchYear, setBatchYear] = useState('');
  const [tSearch, setTSearch] = useState('');
  const [tPage, setTPage] = useState(1);
  const tPerPage = 10;

  const fetchData = async (br, by) => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      if (br) params.set('branch', br);
      if (by) params.set('batch_year', by);
      const qs = params.toString();
      const res = await API.get(`/reports/dashboard${qs ? '?' + qs : ''}`);
      setD(res.data);
    } catch (e) { setError(e.response?.data?.message || 'Failed to load analytics'); }
    finally { setLoading(false); }
  };

  const fetchTests = async () => {
    try {
      const params = new URLSearchParams();
      if (branch) params.set('branch', branch);
      if (batchYear) params.set('batch_year', batchYear);
      const qs = params.toString();
      const res = await API.get(`/reports/tests${qs ? '?' + qs : ''}`);
      setTestsData(res.data);
    } catch { /* non-critical */ }
  };

  useEffect(() => { fetchData(branch, batchYear); fetchTests(); }, []);

  const applyFilters = () => { fetchData(branch, batchYear); };
  const clearFilters = () => { setBranch(''); setBatchYear(''); fetchData('', ''); };

  if (loading) return <div className="axh-page"><div className="axh-loading"><div className="axh-spinner" /><span>Loading analytics…</span></div></div>;
  if (error || !d) return <div className="axh-page"><div className="axh-error"><p>⚠️ {error || 'No data'}</p><button className="axh-btn" onClick={() => fetchData(branch, batchYear)}>Retry</button></div></div>;
  if (d.scoped === false) return <div className="axh-page"><div className="axh-empty">{d.message}</div></div>;

  const { executive: ex, college_health: h, learning: l, behaviour: b, placement: p, intervention: iv, growth: g, system: sys, filter_options: fo } = d;
  const distChart = dist => (dist || []).map(x => ({ name: x.label.split(' ')[0], count: x.count }));

  // Tests tab data
  const filteredTests = (testsData?.tests || []).filter(t => !tSearch || t.test_name?.toLowerCase().includes(tSearch.toLowerCase()));
  const totalTPages = Math.ceil(filteredTests.length / tPerPage);
  const pagedTests = filteredTests.slice((tPage - 1) * tPerPage, tPage * tPerPage);

  return (
    <div className="axh-page">
      <div className="axh-topbar">
        <div>
          <h1>Analytics Hub</h1>
          <p>Enterprise intelligence dashboard — {ex.diagnosed_students}/{ex.total_students} students diagnosed</p>
        </div>
        <div className="axh-topbar-right">
          <button className="axh-btn" onClick={() => { fetchData(branch, batchYear); fetchTests(); }}>↻ Refresh</button>
        </div>
      </div>

      {/* Filters */}
      <div className="axh-filters">
        <span className="axh-filter-label">Filters</span>
        <select className="axh-filter-select" value={branch} onChange={e => setBranch(e.target.value)}>
          <option value="">All Branches</option>
          {(fo?.branches || []).map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <select className="axh-filter-select" value={batchYear} onChange={e => setBatchYear(e.target.value)}>
          <option value="">All Batch Years</option>
          {(fo?.batch_years || []).map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button className="axh-btn primary" onClick={applyFilters}>Apply</button>
        {(branch || batchYear) && <button className="axh-filter-clear" onClick={clearFilters}>Clear</button>}
        {d.filters_applied?.branch && <span className="axh-chip">Branch: {d.filters_applied.branch}</span>}
        {d.filters_applied?.batch_year && <span className="axh-chip">Batch: {d.filters_applied.batch_year}</span>}
      </div>

      {/* Tabs */}
      <div className="axh-tabs" role="tablist">
        {TABS.map(t => (
          <button key={t.key} className={`axh-tab${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)} role="tab" aria-selected={tab === t.key}>{t.label}</button>
        ))}
      </div>

      {/* ═══ TAB: OVERVIEW ═══ */}
      {tab === 'overview' && <>
        <div className="axh-kpi-grid">
          <Kpi label="Total Students" value={ex.total_students} />
          <Kpi label="Diagnostic Coverage" value={ex.diagnostic_completion_pct} suffix="%" tone={sc(ex.diagnostic_completion_pct)} sub={`${ex.diagnosed_students} diagnosed`} />
          <Kpi label="Placement Ready" value={ex.placement_ready} tone="#16a34a" sub={`of ${ex.diagnosed_students}`} />
          <Kpi label="Avg Knowledge" value={ex.avg_knowledge} suffix="%" tone={sc(ex.avg_knowledge)} />
          <Kpi label="Avg Behaviour" value={ex.avg_behaviour} suffix="%" tone={sc(ex.avg_behaviour)} />
          <Kpi label="Avg ERI" value={ex.avg_eri} suffix="%" tone={sc(ex.avg_eri)} />
          <Kpi label="Need Attention" value={ex.students_needing_attention} tone="#dc2626" />
        </div>
        <div className="axh-grid-2">
          <div className="axh-card"><h3>Readiness Levels</h3>
            <Bars items={(h.readiness_levels || []).map(r => ({ label: RL[r.level] || r.level, count: r.count }))} lk="label" vk="count" max={Math.max(1, ...(h.readiness_levels || []).map(r => r.count))} unit="" />
          </div>
          <div className="axh-card"><h3>Department Comparison</h3>
            <div className="axh-table-wrap"><table className="axh-table"><thead><tr><th>Branch</th><th>Diagnosed</th><th>Knowledge</th><th>ERI</th></tr></thead>
              <tbody>{(h.department_comparison || []).map((dep, i) => (
                <tr key={i}><td>{dep.branch}</td><td>{dep.diagnosed}</td><td style={{ color: sc(dep.avg_knowledge) }}>{fmt(dep.avg_knowledge, '%')}</td><td style={{ color: sc(dep.avg_eri) }}>{fmt(dep.avg_eri, '%')}</td></tr>
              ))}</tbody></table></div>
          </div>
        </div>
        <Section title="System Health">
          <div className="axh-kpi-grid">
            <Kpi label="Total Tests" value={sys.total_tests} />
            <Kpi label="Diagnostic Tests" value={sys.diagnostic_tests} />
            <Kpi label="Question Bank" value={sys.question_bank} />
            <Kpi label="Submitted Attempts" value={sys.total_submitted_attempts} />
            <Kpi label="Avg Completion Time" value={sys.avg_completion_time_formatted || fmt(sys.avg_completion_time_sec, 's')} />
            <Kpi label="Avg Attempts/Student" value={sys.avg_attempts_per_student} />
            <Kpi label="Telemetry Coverage" value={sys.telemetry_coverage_pct} suffix="%" tone={sc(sys.telemetry_coverage_pct)} />
          </div>
        </Section>
      </>}

      {/* ═══ TAB: COLLEGE HEALTH ═══ */}
      {tab === 'health' && <>
        <div className="axh-grid-3">
          {[['Knowledge', h.knowledge_distribution], ['Behaviour', h.behaviour_distribution], ['ERI', h.eri_distribution]].map(([name, dist]) => (
            <div className="axh-card" key={name}><h3>{name} Distribution</h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={distChart(dist)} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} /><YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                  <Tooltip /><Bar dataKey="count" radius={[4, 4, 0, 0]}>{distChart(dist).map((_, i) => <Cell key={i} fill={DIST_COLORS[i]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
        <div className="axh-grid-2" style={{ marginTop: 14 }}>
          <div className="axh-card"><h3>Readiness Levels</h3>
            <Bars items={(h.readiness_levels || []).map(r => ({ label: RL[r.level] || r.level, count: r.count }))} lk="label" vk="count" max={Math.max(1, ...(h.readiness_levels || []).map(r => r.count))} unit="" />
          </div>
          <div className="axh-card"><h3>Department Comparison</h3>
            <div className="axh-table-wrap"><table className="axh-table"><thead><tr><th>Branch</th><th>Diagnosed</th><th>Knowledge</th><th>ERI</th></tr></thead>
              <tbody>{(h.department_comparison || []).map((dep, i) => (
                <tr key={i}><td>{dep.branch}</td><td>{dep.diagnosed}</td><td style={{ color: sc(dep.avg_knowledge) }}>{fmt(dep.avg_knowledge, '%')}</td><td style={{ color: sc(dep.avg_eri) }}>{fmt(dep.avg_eri, '%')}</td></tr>
              ))}</tbody></table></div>
          </div>
        </div>
      </>}

      {/* ═══ TAB: LEARNING ═══ */}
      {tab === 'learning' && <>
        <div className="axh-kpi-grid">
          <Kpi label="Question Success Rate" value={l.question_success_rate} suffix="%" tone={sc(l.question_success_rate)} />
          <Kpi label="Avg Time / Question" value={l.avg_time_per_question_sec} suffix="s" />
        </div>
        <div className="axh-grid-2">
          <div className="axh-card"><h3>Weakest Topics</h3><Bars items={l.weakest_topics || []} lk="topic" vk="accuracy" /></div>
          <div className="axh-card"><h3>Strongest Topics</h3><Bars items={l.strongest_topics || []} lk="topic" vk="accuracy" /></div>
        </div>
        <div className="axh-card" style={{ marginTop: 14 }}><h3>Difficulty-wise Accuracy</h3>
          <Bars items={(l.difficulty_performance || []).map(x => ({ label: `${x.difficulty} (${x.attempted})`, accuracy: x.accuracy }))} lk="label" vk="accuracy" />
        </div>
        {(l.topic_accuracy || []).length > 0 && (
          <div className="axh-card" style={{ marginTop: 14 }}><h3>All Topic Accuracy</h3>
            <div className="axh-table-wrap"><table className="axh-table"><thead><tr><th>Topic</th><th>Accuracy</th><th>Attempts</th></tr></thead>
              <tbody>{l.topic_accuracy.map((t, i) => (
                <tr key={i}><td>{t.topic}</td><td style={{ color: sc(t.accuracy) }}>{fmt(t.accuracy, '%')}</td><td>{t.attempts}</td></tr>
              ))}</tbody></table></div>
          </div>
        )}
      </>}

      {/* ═══ TAB: BEHAVIOUR ═══ */}
      {tab === 'behaviour' && <>
        <div className="axh-kpi-grid">
          <Kpi label="Avg TPSI" value={b.avg_tpsi} suffix="%" tone={sc(b.avg_tpsi)} />
          <Kpi label="Avg CER" value={b.avg_cer} suffix="%" tone={sc(b.avg_cer)} />
          <Kpi label="Avg RCI" value={b.avg_rci} suffix="%" tone={sc(b.avg_rci)} />
          <Kpi label="Avg FFI" value={b.avg_ffi} suffix="%" tone={sc(b.avg_ffi)} />
          <Kpi label="Behaviour Score" value={b.avg_behaviour_score} suffix="%" tone={sc(b.avg_behaviour_score)} />
          <Kpi label="Telemetry Coverage" value={b.telemetry_coverage_pct} suffix="%" tone={sc(b.telemetry_coverage_pct)} />
        </div>
        <div className="axh-chips" style={{ marginBottom: 14 }}>
          <span className="axh-chip">Pressure-sensitive: <b>{b.patterns.pressure_sensitive}</b></span>
          <span className="axh-chip">Fatiguers: <b>{b.patterns.fatiguers}</b></span>
          <span className="axh-chip">Speed-dominant: <b>{b.patterns.speed_dominant}</b></span>
          <span className="axh-chip">RCI unmeasured: <b>{b.patterns.risk_unmeasured_rci}</b></span>
        </div>
        <Section title="Growth Analytics" sub={g.note}>
          <div className="axh-kpi-grid">
            <Kpi label="Trackable Students" value={g.trackable_students} />
            <Kpi label="Avg ERI Improvement" value={g.avg_eri_improvement} tone={g.avg_eri_improvement >= 0 ? '#16a34a' : '#dc2626'} />
            <Kpi label="Improving" value={g.improving_students} tone="#16a34a" />
            <Kpi label="Declining" value={g.declining_students} tone="#dc2626" />
          </div>
        </Section>
      </>}

      {/* ═══ TAB: PLACEMENT READINESS ═══ */}
      {tab === 'placement' && <>
        <div className="axh-kpi-grid">
          <Kpi label="Ready" value={p.ready} tone="#16a34a" />
          <Kpi label="Nearly Ready" value={p.nearly_ready} tone="#2563eb" />
          <Kpi label="Needs Improvement" value={p.needs_improvement} tone="#d97706" />
          <Kpi label="Critical" value={p.critical} tone="#dc2626" />
        </div>
        <div className="axh-grid-2">
          <div className="axh-card"><h3>Top Students (by ERI)</h3>
            <div className="axh-table-wrap"><table className="axh-table">
              <thead><tr><th>#</th><th>Name</th><th>Branch</th><th>Knowledge</th><th>ERI</th><th>Level</th></tr></thead>
              <tbody>{(p.top_students || []).map((s, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td className="name-cell" onClick={() => s.user_id && navigate(`/admin/students/${s.user_id}`)}>{s.name || '—'}</td>
                  <td>{s.branch || '—'}</td>
                  <td>{fmt(s.knowledge, '%')}</td>
                  <td style={{ color: sc(s.eri) }}>{fmt(s.eri, '%')}</td>
                  <td><span className="axh-pill" style={{ background: s.level === 'top_performer' ? '#f5f3ff' : '#ecfdf5', color: s.level === 'top_performer' ? '#7c3aed' : '#166534' }}>{RL[s.level] || s.level}</span></td>
                </tr>
              ))}</tbody></table></div>
          </div>
          <div className="axh-card"><h3>Most Improved</h3>
            {(p.most_improved || []).length === 0
              ? <div className="axh-empty">Not enough repeat attempts yet ({p.improvement_trackable_students} trackable)</div>
              : <div className="axh-table-wrap"><table className="axh-table"><thead><tr><th>Branch</th><th>Attempts</th><th>ERI Δ</th></tr></thead>
                  <tbody>{p.most_improved.map((s, i) => <tr key={i}><td>{s.branch}</td><td>{s.attempts}</td><td style={{ color: s.improvement >= 0 ? '#16a34a' : '#dc2626' }}>{s.improvement >= 0 ? '+' : ''}{s.improvement}</td></tr>)}</tbody></table></div>}
          </div>
        </div>
      </>}

      {/* ═══ TAB: INTERVENTION ═══ */}
      {tab === 'intervention' && <>
        <div className="axh-kpi-grid">
          <Kpi label="Low Knowledge" value={iv.low_knowledge} tone="#dc2626" />
          <Kpi label="Low Behaviour" value={iv.low_behaviour} tone="#dc2626" />
          <Kpi label="Low ERI" value={iv.low_eri} tone="#dc2626" />
          <Kpi label="Declining" value={iv.declining_students} tone="#d97706" />
        </div>
        <div className="axh-card">
          {(iv.students || []).length === 0 ? <div className="axh-empty">No students currently flagged for intervention.</div>
            : <div className="axh-table-wrap"><table className="axh-table">
                <thead><tr><th>Name</th><th>Roll No</th><th>Branch</th><th>Knowledge</th><th>Behaviour</th><th>ERI</th><th>Reasons</th></tr></thead>
                <tbody>{iv.students.map((s, i) => (
                  <tr key={i}>
                    <td className="name-cell" onClick={() => s.user_id && navigate(`/admin/students/${s.user_id}`)}>{s.name || '—'}</td>
                    <td>{s.roll_number || '—'}</td>
                    <td>{s.branch || '—'}</td>
                    <td>{fmt(s.knowledge, '%')}</td>
                    <td>{fmt(s.behaviour, '%')}</td>
                    <td style={{ color: sc(s.eri) }}>{fmt(s.eri, '%')}</td>
                    <td>{(s.reasons || []).join(', ')}</td>
                  </tr>
                ))}</tbody></table></div>}
        </div>
      </>}

      {/* ═══ TAB: TEST ANALYTICS ═══ */}
      {tab === 'tests' && <>
        {testsData && (
          <div className="axh-kpi-grid">
            <Kpi label="Total Tests" value={testsData.summary.total_tests} />
            <Kpi label="Published" value={testsData.summary.published_tests} />
            <Kpi label="Draft" value={testsData.summary.draft_tests} />
            <Kpi label="Total Attempts" value={testsData.summary.total_attempts} />
          </div>
        )}
        <div className="axh-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ margin: 0 }}>Test Performance</h3>
            <input type="text" placeholder="Search tests…" value={tSearch} onChange={e => { setTSearch(e.target.value); setTPage(1); }} className="axh-filter-select" style={{ minWidth: 200 }} />
          </div>
          {filteredTests.length === 0 ? <div className="axh-empty">No tests found</div> : <>
            <div className="axh-table-wrap"><table className="axh-table">
              <thead><tr><th>Test Name</th><th>Type</th><th>Qs</th><th>Attempts</th><th>Avg %</th><th>Pass Rate</th><th>Avg Time</th></tr></thead>
              <tbody>{pagedTests.map(t => (
                <tr key={t.id}>
                  <td className="name-cell" onClick={() => navigate(`/admin/tests/${t.id}`)}>{t.test_name}</td>
                  <td><span className="axh-pill" style={{ background: '#f1f5f9', color: '#475569' }}>{t.test_type}</span></td>
                  <td>{t.question_count || 0}</td>
                  <td>{t.attempt_count}</td>
                  <td style={{ color: sc(t.avg_pct) }}>{fmt(t.avg_pct, '%')}</td>
                  <td style={{ color: sc(t.pass_rate) }}>{fmt(t.pass_rate, '%')}</td>
                  <td>{t.avg_time_sec ? (t.avg_time_sec >= 60 ? `${Math.round(t.avg_time_sec / 60)}m` : `${t.avg_time_sec}s`) : '—'}</td>
                </tr>
              ))}</tbody></table></div>
            {totalTPages > 1 && (
              <div className="axh-pagination">
                <span>Showing {(tPage - 1) * tPerPage + 1}–{Math.min(tPage * tPerPage, filteredTests.length)} of {filteredTests.length}</span>
                <div className="axh-page-btns">
                  <button onClick={() => setTPage(p => Math.max(1, p - 1))} disabled={tPage === 1}>Prev</button>
                  <button onClick={() => setTPage(p => Math.min(totalTPages, p + 1))} disabled={tPage === totalTPages}>Next</button>
                </div>
              </div>
            )}
          </>}
        </div>
      </>}

      {/* Data Notes */}
      {(d.data_notes || []).length > 0 && (
        <div className="axh-notes"><strong>Data Notes:</strong><ul>{d.data_notes.map((n, i) => <li key={i}>{n}</li>)}</ul></div>
      )}
    </div>
  );
};

export default AdminAnalytics;
